/**
 * Every write this bundle can make, in one file.
 *
 * The reader half of this pair (endpoints.ts) shipped first and a hostile review
 * found the consequence: 35 POST/PATCH routes built and tested on the Worker, and
 * a client that called none of them. The paths were declared, a test asserted the
 * strings, and nothing ever fetched them. Declaring a path is not wiring it.
 *
 * So the rule this file exists to hold: a station's verb is a function HERE, and
 * the screen calls it. Nothing may settle a scan bar green without a response from
 * one of these having come back 2xx.
 */
import { api } from '../api'
import { paths } from './endpoints'

/** The four reasons on the movement bar, server-validated (MOVEMENT_REASONS). */
export const MOVEMENT_REASONS = ['family', 'gift', 'returned', 'damaged'] as const
export type MovementReason = (typeof MOVEMENT_REASONS)[number]

export interface MovementResult {
  unitId: string
  reason: MovementReason
  state: string
  movementId: string
  isPurchase: boolean
}

export interface CatalogueStartResult {
  unitId: string
  state?: string
  resumed?: boolean
}

/** The write paths the reader file had no reason to name. */
export const writePaths = {
  movementScan: () => '/movement/scan',
  movementSwap: () => '/movement/swap',
  catalogueStart: () => '/catalogue/start',
  catalogueLookup: () => '/catalogue/lookup',
  catalogueChooseTitle: () => '/catalogue/choose-title',
  cataloguePhotoConfirm: () => '/catalogue/photo-confirm',
  catalogueComplete: () => '/catalogue/complete',
  catalogueDefer: () => '/catalogue/defer',
  dropPublish: (dropId: string) => `/drops/${encodeURIComponent(dropId)}/publish`,
} as const

function post<T>(path: string, body: unknown): Promise<T> {
  return api<T>(path, { method: 'POST', body: JSON.stringify(body) })
}

export const writes = {
  /** The five-second bar: one call, one row, the reason echoed back to flash. */
  movementScan: (code: string, reason: MovementReason) =>
    post<MovementResult>(writePaths.movementScan(), { code, reason }),

  catalogueStart: (code: string) => post<CatalogueStartResult>(writePaths.catalogueStart(), { code }),
  catalogueLookup: (unitId: string, isbn: string) =>
    post<{ candidates: Array<{ candidateId: string; name: string }> }>(writePaths.catalogueLookup(), { unitId, isbn }),
  catalogueChooseTitle: (unitId: string, candidateId: string) =>
    post<{ unitId: string; titleId: string }>(writePaths.catalogueChooseTitle(), { unitId, candidateId }),
  cataloguePhotoConfirm: (unitId: string, r2Key: string) =>
    post<{ unitId: string; hasPhoto: boolean }>(writePaths.cataloguePhotoConfirm(), { unitId, r2Key }),
  catalogueComplete: (input: {
    unitId: string
    grade: string
    defectTypeIds?: string[]
    weightG: number
    pricePaise?: number
  }) => post<{ unitId: string; state?: string }>(writePaths.catalogueComplete(), input),
  catalogueDefer: (unitId: string, reason: string) =>
    post<{ deferred: string }>(writePaths.catalogueDefer(), { unitId, reason }),

  pickScan: (roundId: string, code: string) => post<{ unitId?: string }>(paths.pickScan(roundId), { code }),
  packScan: (parcelId: string, code: string) => post<{ unitId?: string }>(paths.packScan(parcelId), { code }),
  closeParcel: (parcelId: string, weightGrams: number) =>
    post<{ parcelId?: string }>(paths.closeParcel(parcelId), { weightGrams }),
  adjudicate: (dropItemId: string, claimId: string) => post<{ awarded?: string }>(paths.adjudicate(dropItemId), { claimId }),
  publishDrop: (dropId: string) => post<{ published?: boolean }>(writePaths.dropPublish(dropId), {}),
} as const
